import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../components/TokenHandler";

const PatientIdLookupModal = ({ open, onClose }) => {
    const { keycloak: kc, givenName, familyName, email } = useAuth();

    const requesterName = [givenName, familyName]
        .filter(Boolean)
        .join(" ");

    const [patientId, setPatientId] = useState("");
    const [patientUniqueId, setPatientUniqueId] = useState("");
    const [lookupResult, setLookupResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const resetModal = () => {
        setPatientId("");
        setPatientUniqueId("");
        setLookupResult(null);
        setError("");
        setLoading(false);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    const handlePatientIdChange = (event) => {
        const value = event.target.value;
        setPatientId(value);

        if (value.trim()) {
            setPatientUniqueId("");
        }
    };

    const handlePatientUniqueIdChange = (event) => {
        const value = event.target.value;
        setPatientUniqueId(value);

        if (value.trim()) {
            setPatientId("");
        }
    };

    const handleLookup = async () => {
        const cleanPatientId = patientId.trim();
        const cleanPatientUniqueId = patientUniqueId.trim();

        setError("");
        setLookupResult(null);

        if (!cleanPatientId && !cleanPatientUniqueId) {
            setError("Enter either a patient ID or a patient unique ID.");
            return;
        }

        const query = cleanPatientId
            ? `patient_id=${encodeURIComponent(cleanPatientId)}`
            : `patient_unique_id=${encodeURIComponent(cleanPatientUniqueId)}`;

        setLoading(true);

        try {
            if (!kc?.authenticated) {
                throw new Error("Your login session is not active. Please sign in again.");
            }

            await kc.updateToken(30);

            if (!kc.token) {
                throw new Error("Unable to obtain an authentication token.");
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/patient-id-lookup?${query}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );

            let data = null;

            try {
                data = await response.json();
            } catch (jsonError) {
                data = null;
            }

            if (!response.ok) {
                throw new Error(
                    data?.error ||
                    `Patient lookup failed with status ${response.status}.`
                );
            }

            setLookupResult(data);
        } catch (lookupError) {
            console.error("Patient ID lookup failed:", lookupError);
            setError(lookupError.message || "Patient lookup failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !loading) {
            handleLookup();
        }
    };

    const facilities = lookupResult?.facilities || [];
    const patientIds = lookupResult?.patient_ids || [];
    const patientUniqueIds = lookupResult?.patient_unique_ids || [];

    const displayValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "Not available";
        }

        return value;
    };

    const FacilityField = ({ label, value }) => (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.25 }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{ fontWeight: 500, overflowWrap: "anywhere" }}
            >
                {displayValue(value)}
            </Typography>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: {
                    height: "92vh",
                    maxHeight: "92vh",
                },
            }}
        >
            <DialogTitle sx={{ pr: 6, position: "relative" }}>
                Patient ID Lookup

                <IconButton
                    aria-label="Close patient ID lookup"
                    onClick={handleClose}
                    sx={{ position: "absolute", right: 12, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflow: "auto",
                }}
            >
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        Search using either a patient ID or a patient unique ID.
                    </Typography>

                    {(requesterName || email) && (
                        <Typography variant="caption" color="text.secondary">
                            Signed in as {requesterName || email}
                            {requesterName && email ? ` (${email})` : ""}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
                        gap: 2,
                        alignItems: "center",
                    }}
                >
                    <TextField
                        label="Patient ID"
                        value={patientId}
                        onChange={handlePatientIdChange}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                        fullWidth
                        inputProps={{ inputMode: "numeric" }}
                    />

                    <TextField
                        label="Patient Unique ID"
                        value={patientUniqueId}
                        onChange={handlePatientUniqueIdChange}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                        fullWidth
                        inputProps={{ inputMode: "numeric" }}
                    />

                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <SearchIcon />
                            )
                        }
                        onClick={handleLookup}
                        disabled={loading}
                        sx={{ minWidth: 130, height: 56 }}
                    >
                        {loading ? "Searching" : "Search"}
                    </Button>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                {lookupResult && (
                    <>
                        <Divider />

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "1fr 1fr 160px",
                                },
                                gap: 2,
                            }}
                        >
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Patient IDs
                                </Typography>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {patientIds.length > 0 ? (
                                        patientIds.map((id) => (
                                            <Chip key={id} label={id} variant="outlined" />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            None found
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Patient Unique IDs
                                </Typography>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {patientUniqueIds.length > 0 ? (
                                        patientUniqueIds.map((id) => (
                                            <Chip key={id} label={id} variant="outlined" />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            None found
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Facilities
                                </Typography>
                                <Typography variant="h5">
                                    {lookupResult.facility_count ?? facilities.length}
                                </Typography>
                            </Paper>
                        </Box>

                        {facilities.length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                                <Typography color="text.secondary">
                                    No facility records found.
                                </Typography>
                            </Paper>
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        lg: "repeat(2, minmax(0, 1fr))",
                                    },
                                    gap: 2,
                                    pb: 1,
                                }}
                            >
                                {facilities.map((facility, index) => {
                                    const patientName = [
                                        facility.first_name,
                                        facility.last_name,
                                    ]
                                        .filter(Boolean)
                                        .join(" ");

                                    return (
                                        <Card
                                            key={[
                                                facility.patient_id,
                                                facility.patient_unique_id,
                                                facility.facility_unique_id,
                                                facility.fac_id,
                                                index,
                                            ].join("-")}
                                            variant="outlined"
                                            sx={{ height: "100%" }}
                                        >
                                            <CardContent>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "flex-start",
                                                        gap: 2,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="h6">
                                                            {displayValue(facility.facility_name)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {displayValue(facility.corp_name)}
                                                        </Typography>
                                                    </Box>

                                                    {facility.status && (
                                                        <Chip
                                                            label={facility.status}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>

                                                <Divider sx={{ mb: 2 }} />

                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "1fr",
                                                            sm: "repeat(2, minmax(0, 1fr))",
                                                            md: "repeat(3, minmax(0, 1fr))",
                                                        },
                                                        gap: 2,
                                                    }}
                                                >
                                                    <FacilityField
                                                        label="Patient ID"
                                                        value={facility.patient_id}
                                                    />
                                                    <FacilityField
                                                        label="Patient Unique ID"
                                                        value={facility.patient_unique_id}
                                                    />
                                                    <FacilityField
                                                        label="Patient"
                                                        value={patientName}
                                                    />
                                                    <FacilityField
                                                        label="Date of Birth"
                                                        value={facility.dob}
                                                    />
                                                    <FacilityField
                                                        label="Facility"
                                                        value={facility.facility_name}
                                                    />
                                                    <FacilityField
                                                        label="Corporation"
                                                        value={facility.corp_name}
                                                    />
                                                    <FacilityField
                                                        label="Source System"
                                                        value={facility.source_system}
                                                    />
                                                    <FacilityField
                                                        label="Provider Number"
                                                        value={facility.provider_number}
                                                    />
                                                    <FacilityField
                                                        label="Status"
                                                        value={facility.status}
                                                    />
                                                    <FacilityField
                                                        label="Admission Date"
                                                        value={facility.admission_date}
                                                    />
                                                    <FacilityField
                                                        label="Discharge Date"
                                                        value={facility.discharge_date}
                                                    />
                                                    <FacilityField
                                                        label="Facility ID"
                                                        value={facility.fac_id}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PatientIdLookupModal;
